/**
 * BilingualHeading — Kaaj signature bilingual heading atom.
 *
 * Renders a Bengali serif heading paired with an uppercase English mono caption.
 * The EN portion is decorative metadata (aria-hidden) per a11y minimums.
 *
 * Decision D3 / AI slop guardrail: BN serif + EN mono UPPERCASE, not centered,
 * no inline style blocks. Uses font-serif (Noto Serif Bengali) + font-mono (JetBrains Mono).
 */
import { cn } from '@repo/ui/lib/utils';
import type { ReactNode } from 'react';

export interface BilingualHeadingProps {
  /** Bengali heading text (primary, semantic). */
  bn: ReactNode;
  /** English caption — rendered UPPERCASE via CSS. */
  en: string;
  /** Heading element. Default 'h2'. */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /** If true, EN caption renders beside BN (inline). Default false (stacked). */
  inline?: boolean;
  /** Size variant. Default 'md'. */
  size?: 'lg' | 'md' | 'sm';
  /** Additional classes for the wrapper element. */
  className?: string;
}

const BN_SIZE: Record<NonNullable<BilingualHeadingProps['size']>, string> = {
  lg: 'text-3xl',
  md: 'text-xl',
  sm: 'text-sm',
};

const EN_SIZE: Record<NonNullable<BilingualHeadingProps['size']>, string> = {
  lg: 'text-[11px]',
  md: 'text-[10px]',
  sm: 'text-[9px]',
};

export function BilingualHeading({
  bn,
  en,
  as: Tag = 'h2',
  inline = false,
  size = 'md',
  className,
}: BilingualHeadingProps) {
  const bnClass = cn('font-serif text-ink leading-tight', BN_SIZE[size]);
  // EN mono caption: UPPERCASE, tracking-wide, ink-mute — decorative metadata
  const enClass = cn(
    'font-mono uppercase tracking-[0.18em] text-ink-mute',
    EN_SIZE[size],
    inline ? 'ml-2 self-end pb-[2px]' : 'mt-0.5',
  );

  return (
    <div className={cn(inline ? 'flex items-baseline' : 'flex flex-col', className)}>
      <Tag lang="bn" className={bnClass}>
        {bn}
      </Tag>
      <span aria-hidden="true" className={enClass}>
        {en}
      </span>
    </div>
  );
}
